import { Component } from '../../scripts/component';
import { ThemeEvents, VariantUpdateEvent } from './events';

class CountdownTimer extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {(event: VariantUpdateEvent) => void} */
    const variantUpdated = (event) => {
      if (event.detail.data.newProduct) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
      }

      const variantId = event.detail.resource?.id ?? null;
      const variantAvailable = Boolean(event.detail.resource?.available);

      if (!variantId || !variantAvailable) {
        this.setAttribute('hidden', '');
        return;
      }

      const variantChanged = variantId !== this.dataset.variantId;
      const wasHidden = this.hasAttribute('hidden');

      this.removeAttribute('hidden');
      this.dataset.variantId = variantId;

      if (variantChanged || wasHidden) {
        this._calculateETA();
      }
    };
    this._calculateETA();

    if (this.dataset.toggleOnVariantAvailability) {
      closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

      this.disconnectedCallback = () => {
        closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
      };
    }
    if (!this.querySelector('.countdown-timer')) {
      const span = document.createElement('strong');
      span.className = 'countdown-timer';
      span.textContent = '--:--:--';
      this.prepend(span);
    }

    this._tick = this._tick.bind(this);
    this._start();
  }

  disconnectedCallback() {
    this._stop();
  }

  get cutoffMs() {
    const [h, m, s] = this.dataset.cutoff.split(':').map(Number);
    const now = new Date();
    now.setHours(h, m, s, 0);
    const seconds = Math.floor(now.getTime() / 1000);

    return seconds * 1000;
  }

  get timerEl() {
    return this.querySelector('.countdown-timer');
  }

  _start() {
    this._stop();
    this._tick();
    this._interval = setInterval(this._tick, 1000);
  }

  _stop() {
    if (this._interval) clearInterval(this._interval);
    this._interval = null;
  }

  _tick() {
    const now = Date.now();
    let diff = Math.max(0, this.cutoffMs - now);

    const hours = String(Math.floor(diff / 1000 / 60 / 60)).padStart(2, '0');
    const minutes = String(Math.floor(diff / 1000 / 60) % 60).padStart(2, '0');
    const seconds = String(Math.floor(diff / 1000) % 60).padStart(2, '0');

    if (this.timerEl) this.timerEl.textContent = `${hours}:${minutes}:${seconds}`;
    this._updateETAMessage();
  }

  _calculateETA() {
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const now = Date.now();

    /** Holidays */
    let holidays = [];
    try {
      holidays = JSON.parse(this.dataset.deliveryNonShipping || '[]').map((d) => new Date(d).setHours(0, 0, 0, 0));
    } catch (e) {
      console.warn('Invalid deliveryNonShipping', e);
    }

    if (!this.dataset.deliveryShipsWeekends) this.dataset.deliveryShipsWeekends = 'false';

    const isWeekend = (ts) => {
      const day = new Date(ts).getDay();
      return day === 0 || day === 6;
    };

    const isHoliday = (ts) => holidays.includes(new Date(ts).setHours(0, 0, 0, 0));

    this.isBusinessDay = (ts) => {
      if (isHoliday(ts)) return false;
      if (this.dataset.deliveryShipsWeekends === 'false' && isWeekend(ts)) return false;
      return true;
    };

    /** Cutoff logic */
    const cutoffDate = new Date(this.cutoffMs);
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

    const todayCutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate(), cutoffDate.getHours(), cutoffDate.getMinutes(), cutoffDate.getSeconds(), cutoffDate.getMilliseconds()).getTime();

    /** Determine start date (Day 1) */
    let startDate = todayMidnight;

    if (this.isBusinessDay(startDate)) {
      if (now > todayCutoff) {
        startDate += ONE_DAY;
        while (!this.isBusinessDay(startDate)) startDate += ONE_DAY;
      }
    } else {
      while (!this.isBusinessDay(startDate)) startDate += ONE_DAY;
    }

    /** Add business days */
    const addBusinessDays = (date, businessDays) => {
      let d = date;
      let count = 0;
      while (count < businessDays) {
        d += ONE_DAY;
        if (this.isBusinessDay(d)) count++;
      }
      return d;
    };

    this.isBeforeCutoff = () => Date.now() <= todayCutoff;

    /** Format helpers */
    this._formatTime = (ts) =>
      new Date(ts).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });

    /** Delivery window */
    const minDays = Number(this.dataset.deliveryBusinessMin);
    const maxDays = Number(this.dataset.deliveryBusinessMax);

    this.deliveryStart = addBusinessDays(startDate, minDays);
    this.deliveryEnd = addBusinessDays(startDate, maxDays);

    /** Run once initially */
    this._updateETAMessage();
  }

  /** Builds the translated ETA message */
  _updateETAMessage() {
    const todayMidnight = new Date().setHours(0, 0, 0, 0);
    if (!this.deliveryStart || !this.deliveryEnd) return;

    const countdownValue = this.timerEl?.textContent ?? '--:--:--';

    this._shortenMonthName = (ts) => {
      const date = new Date(ts);
      const day = date.getDate();
      let month = date.toLocaleDateString('da-DK', {
        month: 'short',
      });
      month = month.charAt(0).toUpperCase() + month.slice(1);

      if (!month.endsWith('.')) {
        month += '.';
      }
      return `${day} ${month}`;
    };

    const formattedStartShorten = this._shortenMonthName(this.deliveryStart);
    const formattedEndShorten = this._shortenMonthName(this.deliveryEnd);

    const countdown = `<span class="text-green">${countdownValue}</span>`;
    const formattedStart = `<span class="text-green">${formattedStartShorten}</span>`;
    const formattedEnd = `<span class="text-green">${formattedEndShorten}</span>`;

    const beforeCutoffTemplate = this.dataset.deliveryBeforeCutoff;
    const afterCutoffTemplate = this.dataset.deliveryAfterCutoff;

    let message;

    if (this.isBeforeCutoff() && this.isBusinessDay(todayMidnight)) {
      message = beforeCutoffTemplate.replace('{{ countdown }}', countdown).replace('{{ start }}', formattedStart).replace('{{ end }}', formattedEnd);
    } else {
      message = afterCutoffTemplate.replace('{{ start }}', formattedStart).replace('{{ end }}', formattedEnd);
    }

    const etaSlot = this.querySelector('.delivery-eta');
    if (etaSlot) etaSlot.innerHTML = message;
    if (this.querySelector('.countdown-timer')) this.querySelector('.countdown-timer').style.display = 'none';
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
